// ─── DeployVerifyStep ──────────────────────────────────────────
// W-9: Final wizard step that creates a test issue to verify the
// full pipeline (webhook → classify → spec → implement → PR).
// Polls /api/projects/:id/verify/status every 5s until the run
// completes or times out after 5 minutes.

import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../api';

interface DeployVerifyStepProps {
  projectId: string;
  onComplete: () => void;
  onSkip: () => void;
}

type VerifyPhase =
  | 'idle'
  | 'creating'
  | 'polling'
  | 'success'
  | 'failed'
  | 'timeout';

interface VerifyState {
  phase: VerifyPhase;
  issueNumber: number | null;
  issueUrl: string | null;
  runId: string | null;
  prNumber: number | null;
  branchName: string | null;
  tasksCompleted: number;
  tasksTotal: number;
  error: string | null;
  elapsed: number;
}

const POLL_INTERVAL = 5_000;
const TIMEOUT = 5 * 60 * 1_000; // 5 minutes

export default function DeployVerifyStep({ projectId, onComplete, onSkip }: DeployVerifyStepProps) {
  const [state, setState] = useState<VerifyState>({
    phase: 'idle',
    issueNumber: null,
    issueUrl: null,
    runId: null,
    prNumber: null,
    branchName: null,
    tasksCompleted: 0,
    tasksTotal: 0,
    error: null,
    elapsed: 0,
  });

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const startVerify = async () => {
    setState(s => ({ ...s, phase: 'creating', error: null }));

    try {
      const result = await api.verifyProject(projectId);
      if (!result.success || !result.issue_number) {
        setState(s => ({
          ...s,
          phase: 'failed',
          error: result.error || 'Failed to create test issue',
        }));
        return;
      }

      const issueNumber = result.issue_number;
      setState(s => ({
        ...s,
        phase: 'polling',
        issueNumber,
        issueUrl: result.issue_url || null,
      }));

      startTimeRef.current = Date.now();

      // Start polling
      pollRef.current = setInterval(async () => {
        const elapsed = Date.now() - startTimeRef.current;

        if (elapsed > TIMEOUT) {
          cleanup();
          setState(s => ({ ...s, phase: 'timeout', elapsed }));
          return;
        }

        try {
          const status = await api.verifyStatus(projectId, issueNumber);

          setState(s => ({
            ...s,
            elapsed,
            runId: status.run_id || s.runId,
            prNumber: status.pr_number ?? s.prNumber,
            branchName: status.branch_name ?? s.branchName,
            tasksCompleted: status.tasks_completed ?? s.tasksCompleted,
            tasksTotal: status.tasks_total ?? s.tasksTotal,
          }));

          if (status.status === 'completed' || status.status === 'merged') {
            cleanup();
            setState(s => ({
              ...s,
              phase: 'success',
              prNumber: status.pr_number ?? s.prNumber,
            }));
          } else if (status.status === 'failed' || status.status === 'error') {
            cleanup();
            setState(s => ({
              ...s,
              phase: 'failed',
              error: `Pipeline run ended with status: ${status.status}`,
            }));
          }
        } catch {
          // Transient error — keep polling
        }
      }, POLL_INTERVAL);
    } catch (err) {
      setState(s => ({
        ...s,
        phase: 'failed',
        error: err instanceof Error ? err.message : 'Unknown error',
      }));
    }
  };

  const formatElapsed = (ms: number) => {
    const s = Math.floor(ms / 1000);
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
  };

  return (
    <div className="wizard-step-content">
      <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 600 }}>
        Deploy & Verify
      </h2>
      <p style={{ color: 'var(--text-dim)', fontSize: 13, margin: '0 0 16px' }}>
        Create a test issue to verify the full pipeline works end-to-end.
      </p>

      {/* Idle — show start button */}
      {state.phase === 'idle' && (
        <div>
          <div className="wizard-info-box" style={{ marginBottom: 16 }}>
            This will create a test issue labeled <code>ocnexus-test</code> on your repo.
            The pipeline should pick it up via webhook, classify it, generate a spec,
            implement the fix, and open a PR. The issue will be closed automatically.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="wizard-btn primary" onClick={startVerify}>
              Run Verification
            </button>
            <button className="wizard-btn" onClick={onSkip}>
              Skip
            </button>
          </div>
        </div>
      )}

      {/* Creating — spinner */}
      {state.phase === 'creating' && (
        <div className="wizard-info-box" style={{ background: 'var(--accent-bg)' }}>
          <span className="spinner" /> Creating test issue...
        </div>
      )}

      {/* Polling — progress */}
      {state.phase === 'polling' && (
        <div>
          <div className="wizard-success-box" style={{ marginBottom: 12 }}>
            Test issue created
            {state.issueUrl && (
              <> — <a href={state.issueUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                #{state.issueNumber}
              </a></>
            )}
          </div>
          <div className="wizard-info-box" style={{ background: 'var(--accent-bg)' }}>
            <div style={{ marginBottom: 8 }}>
              <span className="spinner" /> Waiting for pipeline...
              <span style={{ float: 'right', fontSize: 12, color: 'var(--text-dim)' }}>
                {formatElapsed(state.elapsed)}
              </span>
            </div>
            {state.runId && (
              <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
                Run: <code>{state.runId.slice(0, 8)}</code>
                {state.branchName && <> | Branch: <code>{state.branchName}</code></>}
                {state.tasksTotal > 0 && (
                  <> | Tasks: {state.tasksCompleted}/{state.tasksTotal}</>
                )}
              </div>
            )}
            {/* Progress bar */}
            {state.tasksTotal > 0 && (
              <div style={{
                marginTop: 8,
                height: 4,
                background: 'var(--border)',
                borderRadius: 2,
                overflow: 'hidden',
              }}>
                <div style={{
                  width: `${(state.tasksCompleted / state.tasksTotal) * 100}%`,
                  height: '100%',
                  background: 'var(--accent)',
                  transition: 'width 0.3s ease',
                }} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Success */}
      {state.phase === 'success' && (
        <div>
          <div className="wizard-success-box" style={{ marginBottom: 12 }}>
            Pipeline verified successfully
            {state.prNumber && <> — PR #{state.prNumber} opened</>}
          </div>
          <button className="wizard-btn primary" onClick={onComplete}>
            Continue
          </button>
        </div>
      )}

      {/* Failed */}
      {state.phase === 'failed' && (
        <div>
          <div className="wizard-info-box" style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            marginBottom: 12,
          }}>
            Verification failed: {state.error}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="wizard-btn primary" onClick={startVerify}>
              Retry
            </button>
            <button className="wizard-btn" onClick={onSkip}>
              Skip for now
            </button>
          </div>
        </div>
      )}

      {/* Timeout */}
      {state.phase === 'timeout' && (
        <div>
          <div className="wizard-info-box" style={{
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            marginBottom: 12,
          }}>
            Verification timed out after 5 minutes. The pipeline may still be running.
            {state.runId && <> Run ID: <code>{state.runId.slice(0, 8)}</code></>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="wizard-btn primary" onClick={startVerify}>
              Retry
            </button>
            <button className="wizard-btn" onClick={onSkip}>
              Skip for now
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
